import React, { useCallback, useMemo, useState } from 'react';
import {
  Col,
  notification,
  Row,
  Spin,
  Table,
} from 'antd';
import axios from 'axios';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Permissions from '../../../../auth/Permissions';
import OnTraccrButton from '../../../../common/buttons/OnTraccrButton';
import OnTraccrButtonDropdown from '../../../../common/buttons/OnTraccrButtonDropdown';
import useToggle from '../../../../common/hooks/useToggle';
import FormDetailView from '../../../../forms/CompletedForms/FormDetailView';
import { getFormById } from '../../../../forms/state/forms.actions';
import { textBasedFieldTypes } from '../../../../forms/formHelpers';
import { parseFormResponseToReadableFormat } from '../../../../forms/ResponderHelpers';
import { getProgress } from '../../../../reports/state/reports.actions';
import ReportExcelExport from '../../../../reports/reportExcelExport';
import { currencyFormatter } from '../../../../helpers/inputParsers';
import { request } from '../../../../helpers/requests';
import PayableFormDrawer from '../../../../payables/PayableFormDrawer';
import { getSubcontractColumns } from '../../../ProjectProgress/ProgressColumns';
import ProgressSubContractInfoSlider from '../../../ProjectProgress/ProgressSubContractInfoSlider';
import BudgetTrackingCollapse from '../BudgetTrackingCollapse';
import { getProjectIdMap, getUserIdMap } from '../../../../state/selectors';
import OntraccrTag from '../../../../common/OntraccrTag';
import Colors from '../../../../constants/Colors';

const exporter = new ReportExcelExport();

const EXPORT_MENU_ITEMS = [
  { key: 'excel', label: 'Export to Excel' },
];

const formatExportTimestamp = (ts) => (
  ts != null && typeof ts === 'number'
    ? DateTime.fromMillis(ts).toLocaleString(DateTime.DATETIME_MED)
    : (ts ?? '')
);

export default function CostTrackingSubcontracts({
  subcontracts = [],
  onHistoryClick,
  loadSubcontracts,
  projectId,
  subContractCOs,
  newItemNumbers,
  setNewItemNumbers,
  liveSummaryValues,
}) {
  const dispatch = useDispatch();

  const hasWrite = Permissions.has('PROJECT_PROGRESS_WRITE');

  const { projectProgressFilters } = useSelector((state) => state.projects);
  const { settings } = useSelector((state) => state.settings.company);
  const projectIdMap = useSelector(getProjectIdMap);

  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedSubcontract, setSelectedSubcontract] = useState(null);

  const {
    isToggled: showSubContractInfoSlider,
    toggle: toggleSubContractInfoSlider,
  } = useToggle();

  const {
    isToggled: showFormDetail,
    toggle: toggleFormDetail,
  } = useToggle();

  const {
    isToggled: showPayableFormDrawer,
    toggle: togglePayableFormDrawer,
  } = useToggle();

  const userMap = useSelector(getUserIdMap);

  const closePayableFormDrawer = useCallback(() => {
    togglePayableFormDrawer();
    loadSubcontracts();
    dispatch(getProgress(projectProgressFilters?.dateRange));
  }, [loadSubcontracts, projectProgressFilters?.dateRange, togglePayableFormDrawer]);

  const onInfoClick = useCallback((record) => {
    toggleSubContractInfoSlider();
    setSelectedSubcontract(record);
  }, [toggleSubContractInfoSlider]);

  const sumOfSubContracts = useMemo(
    () => subcontracts.reduce((total, s) => total + (s.contractValue || 0), 0),
    [subcontracts],
  );

  const subcontractColumns = useMemo(() => (
    getSubcontractColumns({
      onHistory: onHistoryClick,
      onInfo: onInfoClick,
    })
  ), [onHistoryClick, onInfoClick]);

  // mostly copied logic from onExport in FormsList.jsx
  const onExport = useCallback(async () => {
    const dataSource = subcontracts.filter((s) => s.formId);
    if (dataSource.length === 0) {
      notification.warn({ message: 'No sub-contracts to export' });
      return;
    }
    const formIds = dataSource.map((s) => s.formId);
    if (formIds.length > 1000) {
      notification.warn({ message: 'Export is limited to 1000 sub-contracts' });
      return;
    }
    setExportLoading(true);
    let exportMaps;
    try {
      const { data = {} } = await request({
        call: axios.post('/forms/exports/data', { ids: formIds }),
        errMsg: 'Failed to get form fields',
        hideSuccessToast: true,
      });
      exportMaps = data;
    } catch (_) {
      setExportLoading(false);
      return;
    }
    const { formMap = {}, templateMap = {} } = exportMaps;
    const existingTemplateNames = new Set();
    const groupedForms = dataSource.reduce((acc, record) => {
      const { formId, templateId } = record;
      if (!templateId) return acc;
      const { [formId]: formData = {} } = formMap;
      const { [templateId]: templateSchema = {} } = templateMap;
      if (!acc[templateId]) {
        const relevantColumns = subcontractColumns
          .map((col) => ({
            title: col.title,
            field: col.exportDataIndex || col.dataIndex,
          }))
          .filter((col) => !!col.title && col.title !== 'History' && col.title !== 'Info');
        templateSchema?.sections?.forEach(({ fields = [] }) => {
          fields?.forEach((field) => {
            const { configProps = {}, selectedType, id: fieldId } = field;
            if (textBasedFieldTypes.has(selectedType)) {
              relevantColumns.push({ title: configProps?.title, field: fieldId });
            }
          });
        });
        relevantColumns.push(
          { title: 'Created On', field: 'createdAt' },
          { title: 'Last Updated', field: 'lastUpdated' },
          { title: 'Status', field: 'status' },
        );
        let adjustedTemplateName = 'Sub-Contract';
        if (existingTemplateNames.has(adjustedTemplateName)) {
          let i = 1;
          while (existingTemplateNames.has(`Sub-Contract (${i})`)) {
            i += 1;
          }
          adjustedTemplateName = `Sub-Contract (${i})`;
        }
        existingTemplateNames.add(adjustedTemplateName);
        acc[templateId] = {
          templateName: adjustedTemplateName,
          columns: relevantColumns,
          data: [],
        };
      }
      acc[templateId].data.push({
        ...record,
        id: formId,
        ...parseFormResponseToReadableFormat({
          templateSections: templateSchema?.sections,
          responseSections: formData?.sections,
          projectIdMap,
          settings: settings ?? {},
          id: formId,
        }),
        createdAt: formatExportTimestamp(record.createdAt),
        lastUpdated: formatExportTimestamp(record.lastUpdated),
        status: record.status ?? '',
      });
      return acc;
    }, {});
    exporter.createFormListExport(Object.values(groupedForms));
    setExportLoading(false);
  }, [
    subcontracts,
    subcontractColumns,
    projectIdMap,
    settings,
  ]);

  const clearFormSelection = useCallback(() => {
    toggleFormDetail();
    loadSubcontracts();
    dispatch(getProgress(projectProgressFilters?.dateRange));
  }, [projectProgressFilters, loadSubcontracts, toggleFormDetail]);

  const onFormClick = useCallback(async (record) => {
    setLoading(true);
    if (await dispatch(getFormById(record.id, { getRecentDraft: true }))) {
      toggleFormDetail();
    }
    setLoading(false);
  }, [toggleFormDetail]);

  return (
    <div id="sub-contracts">
      <BudgetTrackingCollapse
        panelKey="sub-contracts"
        header={(
          <Row justify="space-between" style={{ marginBottom: 16, width: '100%' }} align="middle">
            <Col>
              <span className="budget-tracking-section-title">Sub-Contracts</span>
              <OntraccrTag
                style={{
                  marginLeft: 14,
                  fontFamily: 'open-sans-600',
                  color: Colors.GRAY_950,
                }}
              >
                {currencyFormatter(sumOfSubContracts)}
              </OntraccrTag>
            </Col>
            <Col flex="none" style={{ marginRight: 24 }}>
              <OnTraccrButtonDropdown
                buttonTitle="Export"
                menuItems={EXPORT_MENU_ITEMS.map((item) => ({
                  ...item,
                  disabled: exportLoading,
                }))}
                onMenuClick={onExport}
              />
            </Col>
          </Row>
        )}
      >
        {Permissions.has('PURCHASE_ORDERS_WRITE') && (
          <div style={{ marginBottom: 16 }}>
            <OnTraccrButton
              title="Add Sub-Contract"
              onClick={togglePayableFormDrawer}
              style={{ height: 40 }}
            />
          </div>
        )}
        <Table
          style={{ marginTop: 16 }}
          className="budget-tracking-table"
          size="small"
          columns={subcontractColumns}
          dataSource={subcontracts}
          pagination={false}
          rowClassName="file-table-row"
          onRow={(record) => ({
            onClick: () => onFormClick(record),
          })}
        />
      </BudgetTrackingCollapse>
      <FormDetailView
        visible={showFormDetail}
        onClose={clearFormSelection}
        userMap={userMap}
        shouldRedirect={false}
      />
      <ProgressSubContractInfoSlider
        visible={showSubContractInfoSlider}
        onClose={toggleSubContractInfoSlider}
        record={selectedSubcontract}
        hasWrite={hasWrite}
        projectId={projectId}
        subContractCOs={subContractCOs}
        newItemNumbers={newItemNumbers}
        setNewItemNumbers={setNewItemNumbers}
        liveSummaryValues={liveSummaryValues}
      />
      <PayableFormDrawer
        projectId={projectId}
        type={showPayableFormDrawer ? 'Sub-Contract' : null}
        onClose={closePayableFormDrawer}
      />
      {loading && (
        <Row
          justify="center"
          align="middle"
          className="form-loading-container"
        >
          <Spin size="large" />
        </Row>
      )}
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
CostTrackingSubcontracts.propTypes = {
  subcontracts: PropTypes.array,
  onHistoryClick: PropTypes.func,
  loadSubcontracts: PropTypes.func,
  projectId: PropTypes.string,
  subContractCOs: PropTypes.object,
  newItemNumbers: PropTypes.object,
  setNewItemNumbers: PropTypes.func,
  liveSummaryValues: PropTypes.array,
};

CostTrackingSubcontracts.defaultProps = {
  subcontracts: [],
  onHistoryClick: () => {},
  loadSubcontracts: () => {},
  projectId: null,
  subContractCOs: {},
  newItemNumbers: {},
  setNewItemNumbers: () => {},
  liveSummaryValues: [],
};
